// src/pages/org/OrgView.jsx
import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import { useDialog } from "context/DialogContext";
import OrgDocsTable from "./OrgDocsTable";
import { openDoc } from "lib/docs";

export default function OrgView() {
  const { profile } = useProfile();
  const isAdmin = !!profile?.is_admin;

  const { confirm, alert } = useDialog();

  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [docs, setDocs] = useState([]);

  // Load orgs for admin
  useEffect(() => {
    if (!isAdmin) return;

    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from("orgs")
        .select("id, name")
        .order("name");

      if (cancelled) return;

      if (error) {
        console.error("[OrgView] Error loading org list:", error);
        await alert(error.message || "Failed to load organizations.", {
          title: "Load failed",
          okLabel: "OK",
        });
        setOrgs([]);
        return;
      }

      setOrgs(data || []);
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, alert]);

  const fetchDocs = useCallback(async () => {
    if (!profile) return;

    let query = supabase
      .from("record_docs")
      .select(
        `
          id, title, doc_type, created_at, file_path,
          org:orgs(id, name),
          scopes:record_doc_scopes(
            party:parties(id, canon_name),
            trade:trades(id, trade_id),
            issue:ds_deals(id, name, issuer_name_raw, deal_class)
          )
        `
      )
      .eq("visible_to_org", true)
      .order("created_at", { ascending: false });

    if (selectedOrg) query = query.eq("org_id", selectedOrg);

    const { data, error } = await query;

    if (error) {
      console.error("[OrgView] Error loading record docs:", error);
      await alert(error.message || "Failed to load documents.", {
        title: "Load failed",
        okLabel: "OK",
      });
      return;
    }

    setDocs(data || []);
  }, [profile, selectedOrg, alert]);

  // Refresh docs when profile or org filter changes
  useEffect(() => {
    void fetchDocs();
  }, [fetchDocs]);

  // Listen for refresh events (triggered after uploads)
  useEffect(() => {
    const handler = async () => {
      await fetchDocs();
    };

    document.addEventListener("refresh-org-docs", handler);
    return () => document.removeEventListener("refresh-org-docs", handler);
  }, [fetchDocs]);

  // === Actions ===
  const handleDelete = useCallback(
    async (docId, filePath) => {
      const ok = await confirm("Are you sure you want to delete this document?", {
        title: "Delete document",
        okLabel: "Delete",
        cancelLabel: "Cancel",
      });
      if (!ok) return;

      const { error: docError } = await supabase
        .from("record_docs")
        .delete()
        .eq("id", docId);

      if (docError) {
        console.error("[OrgView] Error deleting record_docs row:", docError);
        await alert(docError.message || "Failed to delete document record.", {
          title: "Delete failed",
          okLabel: "OK",
        });
        return;
      }

      const { error: fileError } = await supabase.storage
        .from("record_docs_files")
        .remove([filePath]);

      if (fileError) {
        console.error("[OrgView] File deleted from DB but not storage:", fileError);
        await alert(
          "The document record was deleted, but the file could not be removed from storage.",
          { title: "Partial delete", okLabel: "OK" }
        );
      }

      // Local state update
      setDocs((prev) => prev.filter((d) => d.id !== docId));

      // 🔔 Also broadcast refresh so AdminView (or others) can update
      document.dispatchEvent(new Event("refresh-org-docs"));
    },
    [confirm, alert]
  );

  return (
    <div className="space-y-4">
      {/* Org selector */}
      {isAdmin && (
        <div className="relative inline-block">
          <select
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value)}
            className="appearance-none pr-9 pl-3 h-10 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                       focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">All Orgs</option>
            {orgs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.2l3.71-2.97a.75.75 0 1 1 .94 1.17l-4.18 3.35a.75.75 0 0 1-.94 0L5.25 8.4a.75.75 0 0 1-.02-1.19z" />
          </svg>
        </div>
      )}

      {/* Table */}
      <OrgDocsTable
        docs={docs}
        isAdmin={isAdmin}
        handleOpen={openDoc}
        handleDelete={handleDelete}
        selectedOrg={selectedOrg}
      />
    </div>
  );
}
